import { DialogTitle } from "@radix-ui/react-dialog";
import { DialogHeader } from "../../utils/components/ui/dialog";
import DropDownComponent from "../common/DropDownComponent";
import { Button } from "../../utils/components/ui/button";
import {
  useAddCategory,
  useGetAllCategory,
  useUpdateCategory,
} from "../../service/categoryService";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  AddCategorySchema,
  AddCategorySchemaT,
} from "../../types/schema/categorySchema";
import { ErrorWraper } from "../common/ErrorWrapper";
import useCategoryRequestStore from "../../store/useCategoryRequest";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { categoryTypes } from "../../utils/constants/constants";
import useGlobalStore from "../../store/useGlobalStore";
import ImageUploader from "../ImageUpload";
import { toast } from "react-toastify";

interface Props {
  setOpen: (open: boolean) => void;
}

function AddNewCategory({ setOpen }: Props) {
  const queryClient = useQueryClient();
  const { cloudImageUrl } = useGlobalStore();
  const { mutate: addNewCategory } = useAddCategory();

  const { currentCategory, setCurrentCategory } = useCategoryRequestStore();

  const { mutate: updateCategory } = useUpdateCategory();

  const [categoryType, setCategoryType] = useState<"main" | "sub">("main");
  const [mainCategoryId, setMainCategoryId] = useState<string | null>(null);

  const { data: categoryList } = useGetAllCategory({
    pageNo: 0,
    pageSize: 10,
    isSubCategory: false,
    enableFlag: categoryType === "sub",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddCategorySchemaT>({
    resolver: zodResolver(AddCategorySchema),
    defaultValues: {
      categoryName: currentCategory?.categoryName || "",
      description: currentCategory?.description || "",
      imageUrl: currentCategory?.imageUrl || "",
      status: currentCategory?.status,
    },
  });

  useEffect(() => {
    if (currentCategory?.isSubCategory && currentCategory?.mainCategory) {
      setMainCategoryId(currentCategory.mainCategory);
    }
    // Optionally, set categoryType as well:
    if (currentCategory?.isSubCategory) {
      setCategoryType("sub");
    } else {
      setCategoryType("main");
    }
  }, [currentCategory]);

  const handleAddCategory = (data: AddCategorySchemaT) => {
    const postData: any = {
      ...data,
      imageUrl: cloudImageUrl || null,
      status: data.status,
    };

    if (categoryType === "sub") {
      postData.mainCategory = Number(mainCategoryId);
      postData.isSubCategory = true;
    } else {
      postData.mainCategory = null;
      postData.isSubCategory = false;
    }

    if (currentCategory?.id) {
      postData.id = currentCategory.id;
      updateCategory(postData, {
        onSuccess: () => {
          setCurrentCategory(null);
          setOpen(false);
          queryClient.invalidateQueries({ queryKey: ["useGetAllCategory"] });
          toast.success("Category Updated successfully!");
        },
        onError: (error: Error) => {
          toast.error(`Failed updating category: ${error.message}`);
          console.error("Error updating category", error);
        },
      });
      return;
    }

    addNewCategory(postData, {
      onSuccess: () => {
        setCurrentCategory(null);
        setOpen(false);
        queryClient.invalidateQueries({ queryKey: ["useGetAllCategory"] });
        toast.success("Category Added successfully!");
      },
      onError: (error: Error) => {
        toast.error(`Failed adding category: ${error.message}`);
        console.error("Error adding category", error);
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentCategory(null);
  };

  // const { data: subCategoryData } = useGetACategory({
  //   categoryId: currentCategory?.id || '',
  //   subCategory: false,
  // });

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[#1A5EF9] text-[20px]">
          Add New Category
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(handleAddCategory)}>
        {/* Category Type Dropdown */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px]">
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Category Type
            </label>
            <DropDownComponent
              options={categoryTypes}
              placeholder="Select a category"
              className="w-full bg-[#F3F4F3] border-none rounded-[2px] my-2"
              value={currentCategory?.isSubCategory ? "sub" : "main"}
              onChange={(val) =>
                setCategoryType(val === "sub" ? "sub" : "main")
              }
            />
          </div>
          {categoryType === "sub" && (
            <div className="flex-1 min-w-[250px]">
              <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
                Main Category
              </label>
              <DropDownComponent
                options={
                  categoryList?.categoryResponse
                    ? categoryList.categoryResponse.map((cat: any) => ({
                        id: cat.id,
                        name: cat.categoryName,
                      }))
                    : []
                }
                placeholder="Select a Main category"
                className="w-full bg-[#F3F4F3] border-none rounded-[2px] my-2"
                value={currentCategory?.mainCategory || mainCategoryId}
                onChange={(val) => setMainCategoryId(val)}
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mt-4">
          <div className="flex-1 min-w-[250px]">
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Category Name
            </label>
            <ErrorWraper message={errors.categoryName?.message}>
              <input
                type="text"
                className="w-full bg-[#F3F4F3] rounded-[2px] mb-4 h-[40px] p-2"
                placeholder="Enter category name"
                {...register("categoryName")}
              />
            </ErrorWraper>
          </div>
          <div className="flex-1 min-w-[250px]">
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Description
            </label>
            <ErrorWraper message={errors.description?.message}>
              <textarea
                className="w-full h-[100px] bg-[#F3F4F3] rounded-[2px] p-2"
                placeholder="Enter category description"
                {...register("description")}
              />
            </ErrorWraper>
          </div>
        </div>

        {/* Image uploader and status */}
        <div className="flex flex-wrap gap-4 mt-4 items-center">
          <div className="flex-1 min-w-[250px]">
            {/* Image uploader logic remains unchanged */}
            <div className="text-[15px] flex rounded-[2px] text-[#84928C] font-[400] text-center border-[#1A5EF9] flex-1 bg-[#F3F4F3] cursor-pointer flex-row items-center justify-center relative">
              {currentCategory?.imageUrl ? (
                <>
                  <img
                    src={currentCategory.imageUrl}
                    alt="category"
                    height={100}
                    width={100}
                    className="object-cover rounded"
                  />
                  <button
                    type="button"
                    className="absolute top-1 right-16 bg-white rounded-full p-1 shadow hover:bg-red-100"
                    onClick={() => {
                      setCurrentCategory({ ...currentCategory, imageUrl: "" });
                    }}
                    title="Remove image"
                    aria-label="Remove image"
                  >
                    <span className="text-lg text-red-500 font-bold">×</span>
                  </button>
                </>
              ) : (
                <ImageUploader />
              )}
            </div>
          </div>
          <div className="flex-1 min-w-[250px] flex flex-col justify-end">
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Status
            </label>
            <div className="flex flex-row items-center mt-2">
              <input
                type="checkbox"
                className="h-[16px] w-[16px]"
                defaultChecked={currentCategory?.status !== false}
                {...register("status")}
              />
              <span className="text-[#1A5EF9] text-[14px] ml-2">Active</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            className="bg-white border cursor-pointer border-[#BAC4C0] text-[#84928C] rounded-[2px] px-6 py-2"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className="bg-[#1A5EF9] text-white cursor-pointer rounded-[2px] px-6 py-2"
            type="submit"
          >
            Save Category
          </Button>
        </div>
      </form>
    </>
  );
}

export default AddNewCategory;
